import React, { KeyboardEvent, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { setGuestAllowed } from '../utils/guestMode';
import { config } from '../config/env';
import { setAuthSession } from '../utils/authStorage';

type AuthUser = {
  id: number;
  username: string;
  email?: string;
  role: string;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  onAuthed: (user: AuthUser) => void;
};

type Mode = 'login' | 'register' | 'forgot' | 'reset';

type PasswordError = {
  ok: boolean;
  message?: string;
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 60;
`;

const Card = styled.div`
  width: min(520px, 100%);
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text};
  border-radius: 12px;
  padding: 1rem;
  box-shadow: ${({ theme }) => theme.hoverShadow};
`;

const Title = styled.div`
  font-weight: 900;
  margin-bottom: 0.25rem;
`;

const Hint = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  line-height: 1.6;
  margin-bottom: 0.85rem;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
`;

const Tab = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ theme, $active }) => ($active ? theme.primary : theme.border)};
  background: ${({ theme, $active }) => ($active ? theme.primary : theme.surfaceAlt)};
  color: ${({ theme, $active }) => ($active ? theme.footerText : theme.text)};
  border-radius: 999px;
  padding: 0.45rem 0.7rem;
  cursor: pointer;
`;

const Row = styled.div`
  display: grid;
  gap: 0.55rem;
`;

const Input = styled.input`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.text};
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.6rem;
  margin-top: 0.9rem;
  flex-wrap: wrap;
`;

const Btn = styled.button<{ $primary?: boolean }>`
  border: 1px solid ${({ theme, $primary }) => ($primary ? theme.primary : theme.border)};
  background: ${({ theme, $primary }) => ($primary ? theme.primary : theme.surface)};
  color: ${({ theme, $primary }) => ($primary ? theme.footerText : theme.text)};
  border-radius: 10px;
  padding: 0.55rem 0.85rem;
  cursor: pointer;

  &:hover {
    background: ${({ theme, $primary }) => ($primary ? theme.primary : theme.surfaceAlt)};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TextBtn = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.primary};
  padding: 0;
  cursor: pointer;
  font: inherit;
`;

const Err = styled.div`
  margin-top: 0.75rem;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
`;

const InlineActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.85rem;
  flex-wrap: wrap;
`;

function validatePassword(password: string): PasswordError {
  if (!password) return { ok: false, message: 'Password is required.' };
  if (!/^(?=.*[a-z])(?=.*\d).{8,}$/.test(password)) {
    return {
      ok: false,
      message: 'Password must be at least 8 characters and include one lowercase letter and one number.',
    };
  }
  return { ok: true };
}

async function postJson<T>(url: string, body?: unknown, token?: string): Promise<T> {
  const base = config.authBaseURL ?? '';
  const fullUrl = base ? `${base.replace(/\/$/, '')}${url}` : url;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(fullUrl, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  let payload: any = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    throw new Error(payload?.message || payload?.error || `HTTP ${res.status}`);
  }

  return payload as T;
}

async function fetchBlogContext(globalToken: string) {
  return postJson<{ appId: string; role: string; initialized: boolean; profile: unknown; accessToken: string }>(
    '/auth/apps/blog/context',
    undefined,
    globalToken,
  );
}

export const AuthChoiceModal: React.FC<Props> = ({ visible, onClose, onAuthed }) => {
  const backdropPressedRef = useRef(false);
  const [mode, setMode] = useState<Mode>('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const canSubmit = useMemo(() => {
    if (mode === 'login') return !!username.trim() && !!password;
    if (mode === 'register') return !!username.trim() && !!email.trim() && !!password;
    if (mode === 'forgot') return !!email.trim();
    return !!email.trim() && !!code.trim() && !!newPassword && !!confirmPassword;
  }, [mode, username, email, password, code, newPassword, confirmPassword]);
  if (!visible) return null;

  const handleOverlayMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    backdropPressedRef.current = event.target === event.currentTarget;
  };

  const handleOverlayMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    const shouldClose = backdropPressedRef.current && event.target === event.currentTarget;
    backdropPressedRef.current = false;
    if (shouldClose) onClose();
  };

  const preventImplicitSubmit = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const switchMode = (nextMode: Mode) => {
    setMode(nextMode);
    setErr(null);
    setInfo(null);
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setCode('');
  };

  const finishAuth = async (globalToken: string, user: { id: number; username: string; email?: string }) => {
    try {
      const ctx = await fetchBlogContext(globalToken);
      const authUser: AuthUser = {
        id: user.id,
        username: user.username,
        email: user.email,
        role: ctx.role,
      };
      setAuthSession(globalToken, ctx.accessToken, authUser);
      setGuestAllowed(false);
      onAuthed(authUser);
      onClose();
    } catch {
      setErr('Failed to load blog access context. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  const doLogin = async () => {
    setLoading(true);
    setErr(null);
    setInfo(null);
    try {
      const res = await postJson<{ access_token: string; user: { id: number; username: string; email?: string } }>('/auth/login', {
        username: username.trim(),
        password,
      });
      await finishAuth(res.access_token, res.user);
    } catch (error) {
      setErr(error instanceof Error ? error.message : 'Login failed.');
      setLoading(false);
    }
  };

  const doRegister = async () => {
    const passwordCheck = validatePassword(password);
    if (!passwordCheck.ok) {
      setErr(passwordCheck.message || 'Invalid password.');
      return;
    }

    setLoading(true);
    setErr(null);
    setInfo(null);
    try {
      const res = await postJson<{ access_token: string; user: { id: number; username: string; email?: string } }>('/auth/register', {
        username: username.trim(),
        email: email.trim(),
        password,
      });
      await finishAuth(res.access_token, res.user);
    } catch (error) {
      setErr(error instanceof Error ? error.message : 'Registration failed.');
      setLoading(false);
    }
  };

  const sendResetCode = async () => {
    setLoading(true);
    setErr(null);
    setInfo(null);
    try {
      await postJson<{ ok: boolean; message: string }>('/auth/password/forgot', { email: email.trim() });
      setInfo('If this email exists, a verification code has been sent.');
      setMode('reset');
    } catch (error) {
      setErr(error instanceof Error ? error.message : 'Failed to send verification code.');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setErr('The two password fields do not match.');
      return;
    }

    const passwordCheck = validatePassword(newPassword);
    if (!passwordCheck.ok) {
      setErr(passwordCheck.message || 'Invalid password.');
      return;
    }

    setLoading(true);
    setErr(null);
    setInfo(null);
    try {
      await postJson<{ ok: boolean; message: string }>('/auth/password/reset', {
        email: email.trim(),
        code: code.trim(),
        newPassword,
      });
      setInfo('Password has been reset. You can now sign in with the new password.');
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setCode('');
      setMode('login');
    } catch (error) {
      setErr(error instanceof Error ? error.message : 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  const renderFields = () => {
    if (mode === 'forgot') {
      return <Input placeholder="Email" type="email" autoComplete="email" value={email} onKeyDown={preventImplicitSubmit} onChange={(e) => setEmail(e.target.value)} />;
    }

    if (mode === 'reset') {
      return (
        <>
          <Input placeholder="Email" type="email" autoComplete="email" value={email} onKeyDown={preventImplicitSubmit} onChange={(e) => setEmail(e.target.value)} />
          <Input placeholder="Verification code" value={code} onKeyDown={preventImplicitSubmit} onChange={(e) => setCode(e.target.value)} />
          <Input placeholder="New password" type="password" autoComplete="new-password" value={newPassword} onKeyDown={preventImplicitSubmit} onChange={(e) => setNewPassword(e.target.value)} />
          <Input
            placeholder="Confirm new password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onKeyDown={preventImplicitSubmit}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </>
      );
    }

    return (
      <>
        <Input placeholder="Username" autoComplete="username" value={username} onKeyDown={preventImplicitSubmit} onChange={(e) => setUsername(e.target.value)} />
        {mode === 'register' ? <Input placeholder="Email" type="email" autoComplete="email" value={email} onKeyDown={preventImplicitSubmit} onChange={(e) => setEmail(e.target.value)} /> : null}
        <Input
          placeholder="Password"
          type="password"
          autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
          value={password}
          onKeyDown={preventImplicitSubmit}
          onChange={(e) => setPassword(e.target.value)}
        />
      </>
    );
  };

  const titleText = mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create account' : mode === 'forgot' ? 'Forgot password' : 'Reset password';
  const hintText =
    mode === 'login'
      ? 'Use your account to sync blog data across devices.'
      : mode === 'register'
        ? 'Create one shared account for blog access and future apps.'
        : mode === 'forgot'
          ? 'Send a verification code to your email. The reset step does not require the old password.'
          : 'Enter the email verification code and choose a new password.';

  return (
    <Overlay onMouseDown={handleOverlayMouseDown} onMouseUp={handleOverlayMouseUp}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Title>{titleText}</Title>
        <Hint>{hintText}</Hint>

        <Tabs>
          <Tab type="button" $active={mode === 'login'} onClick={() => switchMode('login')}>
            Login
          </Tab>
          <Tab type="button" $active={mode === 'register'} onClick={() => switchMode('register')}>
            Register
          </Tab>
          <Tab type="button" $active={mode === 'forgot' || mode === 'reset'} onClick={() => switchMode('forgot')}>
            Forgot password
          </Tab>
        </Tabs>

        <Row>{renderFields()}</Row>

        {mode === 'login' ? (
          <InlineActions>
            <TextBtn type="button" onClick={() => switchMode('forgot')}>
              Forgot password?
            </TextBtn>
          </InlineActions>
        ) : null}

        {info ? <Err>{info}</Err> : null}
        {err ? <Err>{err}</Err> : null}

        <Actions>
          <Btn
            type="button"
            onClick={() => {
              setGuestAllowed(true);
              onClose();
            }}
            title="Guest mode keeps data only in the local browser."
          >
            Continue as guest
          </Btn>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            <Btn type="button" onClick={onClose}>Cancel</Btn>
            <Btn
              type="button"
              $primary
              disabled={!canSubmit || loading}
              onClick={() => {
                void (mode === 'login'
                  ? doLogin()
                  : mode === 'register'
                    ? doRegister()
                    : mode === 'forgot'
                      ? sendResetCode()
                      : resetPassword());
              }}
            >
              {loading
                ? 'Working...'
                : mode === 'login'
                  ? 'Login and sync'
                  : mode === 'register'
                    ? 'Register and sync'
                    : mode === 'forgot'
                      ? 'Send code'
                      : 'Reset password'}
            </Btn>
          </div>
        </Actions>
      </Card>
    </Overlay>
  );
};
